import { useMemo, useState, type ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, ChevronRight } from 'lucide-react';

type Modulo = { id: string; title: string; parentId?: string | null; order?: number };
type Aula = {
  id: string;
  title: string;
  order?: number;
  moduleId?: string | null;
  module_id?: string | null;
  categoryId?: string;
  category_id?: string;
  categoryIds?: string[];
  category_ids?: string[];
};
export interface SelecaoAcesso {
  modulos: string[];
  aulas: string[];
}
type Estado = 'tudo' | 'parte' | 'nada';

const moduloDaAula = (a: Aula) => a.moduleId || a.module_id || null;
const categoriasDaAula = (a: Aula): string[] =>
  a.categoryIds?.length
    ? a.categoryIds
    : a.category_ids?.length
      ? a.category_ids
      : ([a.categoryId || a.category_id].filter(Boolean) as string[]);
const ordenar = <T extends { order?: number; title: string }>(xs: T[]) =>
  [...xs].sort((a, b) => (Number(a.order || 0) - Number(b.order || 0)) || String(a.title).localeCompare(String(b.title)));

/**
 * Árvore de acesso do colaborador, descendo até a aula.
 *
 * O cliente precisava liberar 5 das 10 aulas de uma subpasta, e a árvore
 * antiga parava na pasta. Aqui cada pasta tem três estados: inteira, parte
 * (só o que está marcado dentro) ou nada. O que vai ao servidor é a forma
 * mais curta e sem ambiguidade: pasta inteira vira o id da pasta — e aulas
 * publicadas nela depois também valem —, seleção parcial vira os ids das aulas.
 */
export default function ArvoreDeAcesso({
  categoriasSelecionadas,
  nomeDaCategoria,
  modulosPorCategoria,
  aulas,
  valor,
  aoMudar,
  busca = '',
  idPrefixo,
  extraDoModulo,
}: {
  categoriasSelecionadas: string[];
  nomeDaCategoria: (id: string) => string;
  modulosPorCategoria: Record<string, Modulo[]>;
  aulas: Aula[];
  valor: SelecaoAcesso;
  aoMudar: (v: SelecaoAcesso) => void;
  busca?: string;
  idPrefixo: string;
  /** Algo a mais ao lado de uma pasta inteira (ex.: liberação programada). */
  extraDoModulo?: (moduloId: string) => ReactNode;
}) {
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [fechados, setFechados] = useState<Set<string>>(new Set());

  const indice = useMemo(() => {
    const modulo = new Map<string, Modulo>();
    const filhos = new Map<string, Modulo[]>();
    const raizes = new Map<string, Modulo[]>();
    const aulasDiretas = new Map<string, Aula[]>();
    const soltas = new Map<string, Aula[]>();
    for (const cid of categoriasSelecionadas) {
      const mods = modulosPorCategoria[cid] || [];
      const ids = new Set(mods.map((m) => m.id));
      for (const m of mods) modulo.set(m.id, m);
      raizes.set(cid, ordenar(mods.filter((m) => !m.parentId || !ids.has(m.parentId))));
      for (const m of mods) {
        if (!m.parentId || !ids.has(m.parentId)) continue;
        if (!filhos.has(m.parentId)) filhos.set(m.parentId, []);
        filhos.get(m.parentId)!.push(m);
      }
    }
    for (const [k, v] of filhos) filhos.set(k, ordenar(v));
    for (const a of aulas) {
      const mid = moduloDaAula(a);
      if (mid && modulo.has(mid)) {
        if (!aulasDiretas.has(mid)) aulasDiretas.set(mid, []);
        aulasDiretas.get(mid)!.push(a);
      } else if (!mid) {
        for (const cid of categoriasDaAula(a)) {
          if (!categoriasSelecionadas.includes(cid)) continue;
          if (!soltas.has(cid)) soltas.set(cid, []);
          soltas.get(cid)!.push(a);
        }
      }
    }
    for (const [k, v] of aulasDiretas) aulasDiretas.set(k, ordenar(v));
    for (const [k, v] of soltas) soltas.set(k, ordenar(v));

    // Aulas e pastas de cada subárvore, com proteção contra ciclo.
    const aulasDaSub = new Map<string, string[]>();
    const pastasDaSub = new Map<string, string[]>();
    const montar = (mid: string, caminho: Set<string>) => {
      if (aulasDaSub.has(mid)) return;
      caminho.add(mid);
      const as = (aulasDiretas.get(mid) || []).map((a) => a.id);
      const ps = [mid];
      for (const f of filhos.get(mid) || []) {
        if (caminho.has(f.id)) continue;
        montar(f.id, caminho);
        as.push(...(aulasDaSub.get(f.id) || []));
        ps.push(...(pastasDaSub.get(f.id) || []));
      }
      caminho.delete(mid);
      aulasDaSub.set(mid, as);
      pastasDaSub.set(mid, ps);
    };
    for (const id of modulo.keys()) montar(id, new Set());
    return { modulo, filhos, raizes, aulasDiretas, soltas, aulasDaSub, pastasDaSub };
  }, [categoriasSelecionadas, modulosPorCategoria, aulas]);

  // O que o colaborador enxerga hoje, pela mesma regra do servidor: pasta
  // marcada vale inteira. Cadastros antigos com mãe e filha marcadas aparecem
  // aqui como a mãe inteira — que é o que o aluno vê —, e não como parcial.
  const { marcadas, vaziasMarcadas } = useMemo(() => {
    const marcadas = new Set<string>(valor.aulas);
    const vaziasMarcadas = new Set<string>();
    for (const mid of valor.modulos) {
      if (!indice.modulo.has(mid)) continue;
      const sub = indice.pastasDaSub.get(mid) || [mid];
      const aulasSub = indice.aulasDaSub.get(mid) || [];
      aulasSub.forEach((a) => marcadas.add(a));
      if (aulasSub.length === 0) sub.forEach((d) => vaziasMarcadas.add(d));
    }
    return { marcadas, vaziasMarcadas };
  }, [valor, indice]);

  const estado = (mid: string): Estado => {
    const as = indice.aulasDaSub.get(mid) || [];
    if (as.length === 0) {
      const sub = indice.pastasDaSub.get(mid) || [mid];
      const n = sub.filter((d) => vaziasMarcadas.has(d)).length;
      return n === 0 ? 'nada' : n === sub.length ? 'tudo' : 'parte';
    }
    const n = as.filter((a) => marcadas.has(a)).length;
    return n === 0 ? 'nada' : n === as.length ? 'tudo' : 'parte';
  };

  const emitir = (novas: Set<string>, vazias: Set<string>) => {
    const modulos: string[] = [];
    const escolhidas: string[] = [];
    const vistos = new Set<string>();
    const visitar = (mid: string) => {
      if (vistos.has(mid)) return;
      vistos.add(mid);
      const as = indice.aulasDaSub.get(mid) || [];
      const sub = indice.pastasDaSub.get(mid) || [mid];
      if (as.length > 0 ? as.every((a) => novas.has(a)) : sub.every((d) => vazias.has(d))) {
        modulos.push(mid);
        return;
      }
      for (const f of indice.filhos.get(mid) || []) visitar(f.id);
      for (const a of indice.aulasDiretas.get(mid) || []) if (novas.has(a.id)) escolhidas.push(a.id);
    };
    for (const cid of categoriasSelecionadas) {
      for (const r of indice.raizes.get(cid) || []) visitar(r.id);
      for (const a of indice.soltas.get(cid) || []) {
        if (novas.has(a.id) && !escolhidas.includes(a.id)) escolhidas.push(a.id);
      }
    }
    // O que não aparece nesta árvore fica como estava.
    const exibidas = new Set<string>();
    for (const as of indice.aulasDiretas.values()) as.forEach((a) => exibidas.add(a.id));
    for (const as of indice.soltas.values()) as.forEach((a) => exibidas.add(a.id));
    aoMudar({
      modulos: [...valor.modulos.filter((id) => !indice.modulo.has(id)), ...modulos],
      aulas: [...valor.aulas.filter((id) => !exibidas.has(id)), ...escolhidas],
    });
  };

  const alternarPasta = (mid: string) => {
    const m = new Set(marcadas);
    const v = new Set(vaziasMarcadas);
    const as = indice.aulasDaSub.get(mid) || [];
    const sub = indice.pastasDaSub.get(mid) || [mid];
    if (estado(mid) === 'tudo') {
      as.forEach((a) => m.delete(a));
      sub.forEach((d) => v.delete(d));
    } else {
      as.forEach((a) => m.add(a));
      sub.forEach((d) => { if ((indice.aulasDaSub.get(d) || []).length === 0) v.add(d); });
    }
    emitir(m, v);
  };

  const alternarAula = (aid: string) => {
    const m = new Set(marcadas);
    if (m.has(aid)) m.delete(aid);
    else m.add(aid);
    emitir(m, vaziasMarcadas);
  };

  const termo = busca.trim().toLowerCase();
  const casa = (t: string) => !termo || String(t || '').toLowerCase().includes(termo);
  const casaAbaixo = useMemo(() => {
    const memo = new Map<string, boolean>();
    const f = (mid: string, caminho: Set<string>): boolean => {
      if (memo.has(mid)) return memo.get(mid)!;
      if (caminho.has(mid)) return false;
      caminho.add(mid);
      const m = indice.modulo.get(mid);
      const r = casa(m?.title || '')
        || (indice.aulasDiretas.get(mid) || []).some((a) => casa(a.title))
        || (indice.filhos.get(mid) || []).some((x) => f(x.id, caminho));
      caminho.delete(mid);
      memo.set(mid, r);
      return r;
    };
    return (mid: string) => f(mid, new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice, termo]);

  const aberto = (mid: string) =>
    fechados.has(mid) ? false : abertos.has(mid) || !!termo || estado(mid) === 'parte';
  const alternarAberto = (mid: string) => {
    const agora = aberto(mid);
    setAbertos((s) => { const n = new Set(s); if (agora) n.delete(mid); else n.add(mid); return n; });
    setFechados((s) => { const n = new Set(s); if (agora) n.add(mid); else n.delete(mid); return n; });
  };

  const linhaAula = (a: Aula) => (
    <div key={a.id} className="flex items-center gap-2">
      <Checkbox
        id={`${idPrefixo}-aula-${a.id}`}
        checked={marcadas.has(a.id)}
        onCheckedChange={() => alternarAula(a.id)}
      />
      <label htmlFor={`${idPrefixo}-aula-${a.id}`} className="cursor-pointer text-xs text-muted-foreground">
        {a.title}
      </label>
    </div>
  );

  const pasta = (m: Modulo, paiInteiro: boolean, ancestralCasou: boolean, caminho: Set<string>): ReactNode => {
    if (caminho.has(m.id)) return null;
    const casou = ancestralCasou || casa(m.title);
    if (!casou && !casaAbaixo(m.id)) return null;
    const e = estado(m.id);
    const diretas = indice.aulasDiretas.get(m.id) || [];
    const abre = aberto(m.id);
    const proximo = new Set(caminho).add(m.id);
    return (
      <div key={m.id} className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Checkbox
            id={`${idPrefixo}-pasta-${m.id}`}
            checked={e === 'tudo' ? true : e === 'parte' ? 'indeterminate' : false}
            onCheckedChange={() => alternarPasta(m.id)}
          />
          <label htmlFor={`${idPrefixo}-pasta-${m.id}`} className="cursor-pointer text-sm">
            {m.title}
          </label>
          {diretas.length > 0 && (
            <button
              type="button"
              onClick={() => alternarAberto(m.id)}
              className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
              aria-expanded={abre}
            >
              {abre ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {diretas.length} {diretas.length === 1 ? 'aula' : 'aulas'}
            </button>
          )}
          {e === 'tudo' && !paiInteiro && (
            <span className="rounded bg-primary/10 px-1.5 text-[10px] font-medium text-primary">pasta inteira</span>
          )}
          {e === 'parte' && (
            <span className="rounded bg-amber-100 px-1.5 text-[10px] font-medium text-amber-800">
              só o que está marcado
            </span>
          )}
          {e === 'tudo' && !paiInteiro && extraDoModulo && <span className="ml-auto">{extraDoModulo(m.id)}</span>}
        </div>
        {((indice.filhos.get(m.id) || []).length > 0 || (abre && diretas.length > 0)) && (
          <div className="ml-2 space-y-1.5 border-l pl-3">
            {(indice.filhos.get(m.id) || []).map((f) => pasta(f, e === 'tudo', casou, proximo))}
            {abre && diretas.filter((a) => casou || casa(a.title)).map(linhaAula)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Marcar uma pasta libera tudo o que está nela, inclusive aulas publicadas depois. Para liberar
        só algumas aulas, abra a pasta e marque uma a uma: ela fica como{' '}
        <em>só o que está marcado</em> e o resto não aparece para o colaborador — nem o nome.
      </p>
      <ScrollArea className="h-[320px] w-full rounded-md border p-4">
        <div className="space-y-4">
          {categoriasSelecionadas.map((cid) => {
            const raizes = indice.raizes.get(cid) || [];
            const soltas = (indice.soltas.get(cid) || []).filter((a) => casa(a.title));
            return (
              <div key={cid} className="space-y-1.5">
                <div className="text-xs font-medium text-muted-foreground">{nomeDaCategoria(cid)}</div>
                {raizes.length === 0 && soltas.length === 0 && (
                  <div className="text-xs text-muted-foreground">Nenhuma pasta nesta categoria.</div>
                )}
                {raizes.map((r) => pasta(r, false, false, new Set()))}
                {soltas.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Aulas fora de pasta</div>
                    {soltas.map(linhaAula)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
